import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AuthNavbar from "../components/AuthNavbar";
import LanguageSelector from "../components/LanguageSelector";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { countryPhoneCodes } from "../data/country-phone-code";
import CustomSnackbar from "../components/CustomSnackbar";
import DotPulseLoading from "../components/Loading/DotPulseLoading";
import WhatsNewSection from "../components/WhatsNewSection";
import { getmessage } from "../utils/i18messages";

const EyeIcon = ({ show }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    {show ? (
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    ) : (
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    )}
  </svg>
);

// const GoogleIcon = () => (
//   <svg width="20" height="20" viewBox="0 0 24 24">
//     <path
//       d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
//       fill="#4285F4"
//     />
//     <path
//       d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
//       fill="#34A853"
//     />
//     <path
//       d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
//       fill="#FBBC05"
//     />
//     <path
//       d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
//       fill="#EA4335"
//     />
//   </svg>
// );

// const SSOIcon = () => (
//   <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
//     <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
//   </svg>
// );

const fadeIn = {
  hidden: { opacity: 0, y: 20 },

  visible: {
    opacity: 1,

    y: 0,

    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemFadeIn = {
  hidden: { opacity: 0, y: 15 },

  visible: { opacity: 1, y: 0 },
};

// Password strength calculator

const calculatePasswordStrength = (password) => {
  let strength = 0;

  const checks = {
    hasLength: password.length >= 8,

    hasUpperCase: /[A-Z]/.test(password),

    hasLowerCase: /[a-z]/.test(password),

    hasNumber: /\d/.test(password),

    hasSpecial: /[@$!%*?&#]/.test(password),
  };

  if (checks.hasLength) strength += 20;

  if (checks.hasUpperCase) strength += 20;

  if (checks.hasLowerCase) strength += 20;

  if (checks.hasNumber) strength += 20;

  if (checks.hasSpecial) strength += 20;

  return {
    strength: Math.min(strength, 100),

    checks,
  };
};

const PasswordStrengthIndicator = ({ strength, checks, t }) => {
  const strengthLabel =
    strength < 40
      ? t("password_strength.weak")
      : strength < 60
      ? t("password_strength.medium")
      : strength < 80
      ? t("password_strength.strong")
      : t("password_strength.very_strong");

  const strengthColor =
    strength < 40
      ? "#ef5350" // red
      : strength < 60
      ? "#fb8c00" // orange
      : strength < 80
      ? "#fdd835" // yellow
      : "#66bb6a"; // green

  const criteria = [
    {
      label: t("password_strength.8_chars"),

      met: checks.hasLength,
    },

    {
      label: t("password_strength.uppercase"),

      met: checks.hasUpperCase,
    },

    {
      label: t("password_strength.lowercase"),

      met: checks.hasLowerCase,
    },

    { label: t("password_strength.number"), met: checks.hasNumber },

    {
      label: t("password_strength.special_char"),

      met: checks.hasSpecial,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      animate={{
        opacity: 1,

        height: "auto",

        marginTop: "-10px",

        marginBottom: "24px",
      }}
      exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          display: "flex",

          alignItems: "center",

          marginBottom: "12px",

          paddingTop: "10px",
        }}
      >
        <div
          style={{
            flex: 1,

            height: "8px",

            backgroundColor: "#e0e0e0",

            borderRadius: "4px",

            overflow: "hidden",
          }}
        >
          <motion.div
            style={{
              height: "100%",

              backgroundColor: strengthColor,

              borderRadius: "4px",
            }}
            initial={{ width: "0%" }}
            animate={{ width: `${strength}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>

        <span
          style={{
            marginLeft: "12px",

            fontSize: "14px",

            fontWeight: "600",

            color: strengthColor,
          }}
        >
          {strengthLabel}
        </span>
      </div>

      <div style={{ fontSize: "13px", color: "#718096" }}>
        <ul
          style={{
            paddingLeft: "0px",

            marginTop: "8px",

            listStyleType: "none",

            display: "grid",

            gridTemplateColumns: "1fr 1fr",

            gap: "4px 8px",
          }}
        >
          {criteria.map((item, index) => (
            <motion.li
              key={index}
              style={{
                color: item.met ? "#66bb6a" : "#718096",

                display: "flex",

                alignItems: "center",

                gap: "6px",
              }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={item.met ? 3 : 2}
              >
                {item.met ? (
                  <path d="M20 6L9 17l-5-5" />
                ) : (
                  <path d="M18 6L6 18M6 6l12 12" />
                )}
              </svg>

              {item.label}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

function Register() {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [selectedCode, setSelectedCode] = useState("+62");

  const [agreed, setAgreed] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",

    email: "",

    country: "Indonesia",

    phone: "",

    password: "",

    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,

    message: "",

    severity: "success",
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 968);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const passwordStrength = calculatePasswordStrength(formData.password);

  const handleCountryChange = (e) => {
    const countryName = e.target.value;

    const country = countryPhoneCodes.find((c) => c.name === countryName);

    setFormData((prev) => ({
      ...prev,

      country: countryName,
    }));

    if (country) {
      setSelectedCode(country.code);
    } else {
      setSelectedCode("+62");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lang = localStorage.getItem("i18nextLng") || "en";

    // Validation

    if (formData.password !== formData.confirmPassword) {
      setSnackbar({
        open: true,

        message: getmessage("profile_settings.password_mismatch"),

        severity: "error",
      });

      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!passwordRegex.test(formData.password)) {
      setSnackbar({
        open: true,

        message: getmessage("profile_settings.password_invalid"),

        severity: "error",
      });

      return;
    }

    if (!agreed) {
      setSnackbar({
        open: true,

        message: getmessage("register.mustAgreeToTerms"),

        severity: "error",
      });

      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: formData.email,

        password: formData.password,

        fullName: formData.fullName,

        phoneCode: selectedCode,

        phone: formData.phone,

        country: formData.country,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/sign-up`,

        payload,

        {
          headers: {
            "Content-Type": "application/json",

            "Accept-Language": lang,
          },
        }
      );

      setSnackbar({
        open: true,

        message:
          response.data.message ||
          "Successful registration. Please check your email to activate your account.",

        severity: "success",
      });

      // Redirect to login page

      setTimeout(() => {
        navigate("/login");
      }, 3000);

      // Reset form after successful registration

      setFormData({
        fullName: "",

        email: "",

        country: "",

        phone: "",

        password: "",

        confirmPassword: "",
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";

      setSnackbar({
        open: true,

        message: errorMessage,

        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      {/* Navbar Component */}

      <AuthNavbar />

      <div
        style={{
          minHeight: "100vh",

          backgroundColor: "#ffffff",

          display: "flex",

          position: "relative",

          overflow: "hidden",
        }}
      >
        <style>{`

        @keyframes pulse {

          0%, 100% { opacity: 1; transform: scale(1); }

          50% { opacity: 0.5; transform: scale(0.8); }

        }

        @keyframes float {

          0%, 100% { transform: translateY(0px); }

          50% { transform: translateY(-20px); }

        }

        @keyframes floatSlow {

          0%, 100% { transform: translate(0, 0); }

          50% { transform: translate(15px, -15px); }

        }

      `}</style>

        {/* Loading Overlay */}

        {loading && (
          <div
            style={{
              position: "fixed",

              top: 0,

              left: 0,

              right: 0,

              bottom: 0,

              backgroundColor: "rgba(0,0,0,0.5)",

              zIndex: 9999,

              display: "flex",

              alignItems: "center",

              justifyContent: "center",
            }}
          >
            <DotPulseLoading size={14} color="#d4a574" />
          </div>
        )}

        {/* Abstract Bubble Background - Left Side */}

        <div
          style={{
            position: "absolute",

            left: "-25%",

            top: "40%",

            transform: "translateY(-50%)",

            width: isMobile ? "150%" : "85%",

            height: "150%",

            zIndex: 0,

            pointerEvents: "none",
          }}
        >
          {/* Main Large Bubble */}

          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",

              height: "100%",

              filter: "drop-shadow(0 10px 40px rgba(212, 165, 116, 0.1))",
            }}
          >
            <defs>
              <linearGradient
                id="gradient1"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />

                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />

                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            {/* Wavy organic blob shape */}

            <path
              d="M 150,100 

               C 150,50 200,20 280,50

               C 360,80 400,120 450,200

               C 500,280 520,360 480,450

               C 440,540 400,600 350,680

               C 300,760 250,820 180,860

               C 110,900 50,880 30,800

               C 10,720 -20,640 20,540

               C 60,440 100,360 120,260

               C 140,160 150,150 150,100 Z"
              fill="url(#gradient1)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>

          {/* Smaller floating bubbles */}

          <div
            style={{
              position: "absolute",

              top: "15%",

              right: "10%",

              width: "120px",

              height: "120px",

              borderRadius: "50%",

              background:
                "radial-gradient(circle at 30% 30%, rgba(232, 207, 179, 0.4), rgba(212, 165, 116, 0.2))",

              animation: "floatSlow 6s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",

              bottom: "20%",

              right: "25%",

              width: "80px",

              height: "80px",

              borderRadius: "50%",

              background:
                "radial-gradient(circle at 30% 30%, rgba(241, 228, 209, 0.5), rgba(232, 207, 179, 0.3))",

              animation: "floatSlow 7s ease-in-out infinite reverse",
            }}
          />

          <div
            style={{
              position: "absolute",

              top: "45%",

              right: "5%",

              width: "60px",

              height: "60px",

              borderRadius: "50%",

              background:
                "radial-gradient(circle at 30% 30%, rgba(250, 246, 241, 0.6), rgba(241, 228, 209, 0.4))",

              animation: "float 5s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",

              bottom: "20%",

              right: "10%",

              width: "70px",

              height: "70px",

              borderRadius: "50%",

              background:
                "radial-gradient(circle at 30% 30%, rgba(232, 207, 179, 0.5), rgba(212, 165, 116, 0.3))",

              animation: "floatSlow 6s ease-in-out infinite reverse",
            }}
          />
        </div>

        {/* Main Large Bubble - Right Bottom */}

        <div
          style={{
            position: "absolute",

            right: "-25%",

            bottom: "10%",

            width: isMobile ? "150%" : "85%",

            height: "150%",

            zIndex: 0,

            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",

              height: "100%",

              filter: "drop-shadow(0 10px 40px rgba(212, 165, 116, 0.1))",

              opacity: 0.2,
            }}
          >
            <defs>
              <linearGradient
                id="gradient2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />

                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />

                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            {/* Wavy organic blob shape - same as left */}

            <path
              d="M 150,100 

               C 150,50 200,20 280,50

               C 360,80 900,120 450,200

               C 500,280 520,360 480,450

               C 440,540 400,600 350,680

               C 300,760 250,820 180,860

               C 110,900 50,880 30,800

               C 10,720 -20,640 20,540

               C 60,440 100,360 120,260

               C 140,160 150,150 150,100 Z"
              fill="url(#gradient2)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>
        </div>

        {/* Bubble - Right Top */}

        <div
          style={{
            position: "absolute",

            right: "5%",

            top: "10%",

            width: "200px",

            height: "200px",

            zIndex: 0,

            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 200 200"
            style={{
              width: "100%",

              height: "100%",

              filter: "drop-shadow(0 5px 20px rgba(212, 165, 116, 0.1))",

              opacity: 0.3,
            }}
          >
            <defs>
              <linearGradient
                id="gradient3"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />

                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />

                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            <circle
              cx="100"
              cy="100"
              r="80"
              fill="url(#gradient3)"
              style={{
                animation: "floatSlow 7s ease-in-out infinite",
              }}
            />
          </svg>
        </div>

        {/* Bubble - Center Right */}

        <div
          style={{
            position: "absolute",

            right: "15%",

            top: "50%",

            transform: "translateY(-50%)",

            width: "150px",

            height: "150px",

            zIndex: 0,

            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 150 150"
            style={{
              width: "100%",

              height: "100%",

              filter: "drop-shadow(0 5px 20px rgba(212, 165, 116, 0.1))",

              opacity: 0.4,
            }}
          >
            <defs>
              <linearGradient
                id="gradient4"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />

                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />

                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            <ellipse
              cx="75"
              cy="75"
              rx="60"
              ry="45"
              fill="url(#gradient4)"
              style={{
                animation: "float 6s ease-in-out infinite reverse",
              }}
            />
          </svg>
        </div>

        {/* Left Sidebar - What's New (Hidden on mobile) */}

        <WhatsNewSection isMobile={isMobile} />

        {/* Right Side - Register Form */}

        <div
          style={{
            flex: 1,

            display: "flex",

            flexDirection: "column",

            alignItems: "center",

            justifyContent: "center",

            padding: isMobile ? "32px 24px" : "48px",

            position: "relative",

            zIndex: 1,
          }}
        >
          {/* Register Form Container */}

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            style={{
              width: "100%",

              maxWidth: "480px",

              marginTop: "0",
            }}
          >
            <motion.div
              variants={itemFadeIn}
              style={{
                display: "flex",

                justifyContent: "center",

                marginBottom: "24px",

                position: "relative",

                zIndex: 20,
              }}
            >
              <LanguageSelector />
            </motion.div>

            <motion.h1
              variants={itemFadeIn}
              style={{
                fontSize: isMobile ? "32px" : "40px",

                fontWeight: "bold",

                color: "#1a202c",

                marginBottom: "12px",

                textAlign: "center",
              }}
            >
              {t("register.title")}
            </motion.h1>

            <motion.p
              variants={itemFadeIn}
              style={{
                fontSize: "16px",

                color: "#718096",

                textAlign: "center",

                marginBottom: "40px",
              }}
            >
              {t("register.subtitle")}
            </motion.p>

            <form onSubmit={handleSubmit}>
              {/* Full Name Input */}

              <motion.div
                variants={itemFadeIn}
                style={{ marginBottom: "24px" }}
              >
                <label
                  style={{
                    display: "block",

                    fontSize: "14px",

                    fontWeight: "600",

                    color: "#4a5568",

                    marginBottom: "8px",

                    textTransform: "uppercase",

                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.fullNameLabel")}
                </label>

                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  placeholder={t("register.fullNamePlaceholder")}
                  style={{
                    width: "100%",

                    padding: "14px 16px",

                    fontSize: "15px",

                    border: "1px solid #d1d5db",

                    borderRadius: "8px",

                    backgroundColor: "#ffffff",

                    transition: "all 0.3s ease",

                    outline: "none",

                    fontFamily: "inherit",

                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#d4a574";

                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(212, 165, 116, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";

                    e.target.style.boxShadow = "none";
                  }}
                />
              </motion.div>

              {/* Email Input */}

              <motion.div
                variants={itemFadeIn}
                style={{ marginBottom: "24px" }}
              >
                <label
                  style={{
                    display: "block",

                    fontSize: "14px",

                    fontWeight: "600",

                    color: "#4a5568",

                    marginBottom: "8px",

                    textTransform: "uppercase",

                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.emailLabel")}
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="user@mail.com"
                  style={{
                    width: "100%",

                    padding: "14px 16px",

                    fontSize: "15px",

                    border: "1px solid #d1d5db",

                    borderRadius: "8px",

                    backgroundColor: "#ffffff",

                    transition: "all 0.3s ease",

                    outline: "none",

                    fontFamily: "inherit",

                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#d4a574";

                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(212, 165, 116, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";

                    e.target.style.boxShadow = "none";
                  }}
                />
              </motion.div>

              {/* Country Select */}

              <motion.div
                variants={itemFadeIn}
                style={{ marginBottom: "24px" }}
              >
                <label
                  style={{
                    display: "block",

                    fontSize: "14px",

                    fontWeight: "600",

                    color: "#4a5568",

                    marginBottom: "8px",

                    textTransform: "uppercase",

                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.countryLabel")}
                </label>

                <select
                  name="country"
                  value={formData.country}
                  onChange={handleCountryChange}
                  required
                  style={{
                    width: "100%",

                    padding: "14px 16px",

                    fontSize: "15px",

                    border: "1px solid #d1d5db",

                    borderRadius: "8px",

                    backgroundColor: "#ffffff",

                    transition: "all 0.3s ease",

                    outline: "none",

                    fontFamily: "inherit",

                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#d4a574";

                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(212, 165, 116, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";

                    e.target.style.boxShadow = "none";
                  }}
                >
                  {countryPhoneCodes.map((country, index) => (
                    <option key={`${country.code}-${index}`} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Phone Input */}

              <motion.div
                variants={itemFadeIn}
                style={{ marginBottom: "24px" }}
              >
                <label
                  style={{
                    display: "block",

                    fontSize: "14px",

                    fontWeight: "600",

                    color: "#4a5568",

                    marginBottom: "8px",

                    textTransform: "uppercase",

                    letterSpacing: "0.5px",
                  }}
                >
                  {t("register.phoneLabel")}
                </label>

                <div style={{ position: "relative" }}>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="81234567890"
                    style={{
                      width: "100%",

                      padding: "14px 16px 14px 60px",

                      fontSize: "15px",

                      border: "1px solid #d1d5db",

                      borderRadius: "8px",

                      backgroundColor: "#ffffff",

                      transition: "all 0.3s ease",

                      outline: "none",

                      fontFamily: "inherit",

                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#d4a574";

                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(212, 165, 116, 0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#d1d5db";

                      e.target.style.boxShadow = "none";
                    }}
                  />

                  <span
                    style={{
                      position: "absolute",

                      left: "16px",

                      top: "50%",

                      transform: "translateY(-50%)",

                      color: "#718096",

                      fontSize: "15px",
                    }}
                  >
                    {selectedCode}
                  </span>
                </div>
              </motion.div>

              {/* Password & Confirm Password Inputs */}

              <motion.div
                variants={itemFadeIn}
                style={{
                  display: "flex",

                  flexDirection: isMobile ? "column" : "row",

                  gap: "24px",

                  marginBottom: "24px",
                }}
              >
                {/* Password Input */}

                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",

                      fontSize: "14px",

                      fontWeight: "600",

                      color: "#4a5568",

                      marginBottom: "8px",

                      textTransform: "uppercase",

                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("register.passwordLabel")}
                  </label>

                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="••••••"
                      style={{
                        width: "100%",

                        padding: "14px 48px 14px 16px",

                        fontSize: "15px",

                        border: "1px solid #d1d5db",

                        borderRadius: "8px",

                        backgroundColor: "#ffffff",

                        transition: "all 0.3s ease",

                        outline: "none",

                        fontFamily: "inherit",

                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#d4a574";

                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(212, 165, 116, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";

                        e.target.style.boxShadow = "none";
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",

                        right: "12px",

                        top: "50%",

                        transform: "translateY(-50%)",

                        background: "none",

                        border: "none",

                        cursor: "pointer",

                        color: "#718096",

                        padding: "8px",

                        display: "flex",

                        alignItems: "center",
                      }}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                </div>

                {/* Confirm Password Input */}

                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",

                      fontSize: "14px",

                      fontWeight: "600",

                      color: "#4a5568",

                      marginBottom: "8px",

                      textTransform: "uppercase",

                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("register.confirmPasswordLabel")}
                  </label>

                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      placeholder="••••••"
                      style={{
                        width: "100%",

                        padding: "14px 48px 14px 16px",

                        fontSize: "15px",

                        border: "1px solid #d1d5db",

                        borderRadius: "8px",

                        backgroundColor: "#ffffff",

                        transition: "all 0.3s ease",

                        outline: "none",

                        fontFamily: "inherit",

                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#d4a574";

                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(212, 165, 116, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";

                        e.target.style.boxShadow = "none";
                      }}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{
                        position: "absolute",

                        right: "12px",

                        top: "50%",

                        transform: "translateY(-50%)",

                        background: "none",

                        border: "none",

                        cursor: "pointer",

                        color: "#718096",

                        padding: "8px",

                        display: "flex",

                        alignItems: "center",
                      }}
                    >
                      <EyeIcon show={showConfirmPassword} />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Password Strength Indicator */}

              <AnimatePresence>
                {formData.password && (
                  <PasswordStrengthIndicator
                    strength={passwordStrength.strength}
                    checks={passwordStrength.checks}
                    t={t}
                  />
                )}
              </AnimatePresence>

              {/* Agreement Checkbox */}

              <motion.div
                variants={itemFadeIn}
                style={{ marginBottom: "22px" }}
              >
                <div
                  style={{
                    display: "flex",

                    alignItems: "flex-start",

                    fontSize: "14px",

                    color: "#4a5568",

                    gap: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    style={{
                      marginTop: "2px",

                      flexShrink: 0,

                      justifyContent: "center",

                      alignItems: "center",
                    }}
                  />

                  <span>
                    {t("register.agreePrefix")}{" "}
                    <a
                      href="/term-conditions"
                      style={{
                        color: "#d4a574",

                        textDecoration: "none",
                      }}
                    >
                      {t("register.terms")}
                    </a>
                    {" and "}
                    <a
                      href="/privacy-policy"
                      style={{
                        color: "#d4a574",

                        textDecoration: "none",
                      }}
                    >
                      {t("register.privacy")}
                    </a>
                  </span>
                </div>
              </motion.div>

              {/* Submit Button */}

              <motion.button
                variants={itemFadeIn}
                type="submit"
                disabled={loading || !agreed}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                style={{
                  width: "100%",

                  padding: "16px",

                  fontSize: "16px",

                  fontWeight: "600",

                  color: "white",

                  backgroundColor: "#d4a574",

                  border: "none",

                  borderRadius: "8px",

                  cursor: loading || !agreed ? "not-allowed" : "pointer",

                  opacity: loading || !agreed ? 0.7 : 1,

                  marginBottom: "24px",

                  fontFamily: "inherit",

                  transition: "all 0.3s ease",
                }}
              >
                {loading
                  ? "Creating Account..."
                  : t("register.createAccountButton")}
              </motion.button>

              {/* Links */}

              {/* <motion.div
                variants={itemFadeIn}
                style={{
                  display: "flex",

                  justifyContent: "center",

                  fontSize: "14px",

                  marginBottom: "32px",
                }}
              >
                <a
                  href="/login"
                  style={{
                    color: "#1976d2",

                    textDecoration: "none",

                    fontWeight: "500",
                  }}
                >
                  {t("register.haveAccountText")} {t("register.signInButton")}
                </a>
              </motion.div> */}
            </form>
          </motion.div>
        </div>

        {/* Snackbar */}

        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </>
  );
}

export default Register;
